import Checkbox from '@atlaskit/checkbox';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import {
  deselectAll,
  selectEntries,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import React, { useCallback } from 'react';

import { type EntryIsSelectedMap } from './EntryPrefix';

export interface HeaderCheckboxProps {
  entryIsSelectedMap: EntryIsSelectedMap;
  contentId: string;
}

const allCheckboxesUnselected = (entryIsSelectedMap: EntryIsSelectedMap) => {
  return Object.values(entryIsSelectedMap).every((value) => value === false);
};

const HeaderCheckbox: React.FC<HeaderCheckboxProps> = ({
  entryIsSelectedMap,
  contentId,
}) => {
  const { referencing } = useDatabaseContent();
  const analyticsClient = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();
  const databaseContainer = getDatabaseContainerType();
  const handleHeaderCheckboxSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Remove focus to prevent focus ring from showing
      e.target.blur();
      const allUnselected = allCheckboxesUnselected(entryIsSelectedMap);
      const entryIds = Object.keys(entryIsSelectedMap);
      analyticsClient.sendHeaderCheckboxSelectedEvent({
        contentId,
        databaseContainer,
        entriesCount: entryIds.length,
        checkboxState: allUnselected ? 'selected' : 'unselected',
      });

      if (allUnselected) {
        dispatch(
          selectEntries({
            contentId,
            entryIds: entryIds,
            clearSelectedValues: false,
          })
        );
      } else {
        dispatch(deselectAll());
      }
      dispatch(setSoftSelection({ value: false }));
    },
    [entryIsSelectedMap, analyticsClient, contentId, databaseContainer, dispatch]
  );

  const selectedValues = Object.values(entryIsSelectedMap);
  const isChecked = selectedValues.length > 0 && selectedValues.every((value) => value);
  const isIndeterminate =
    selectedValues.length > 0 &&
    selectedValues.some((value) => value) &&
    selectedValues.some((value) => !value);

  return (
    <Checkbox
      onChange={handleHeaderCheckboxSelected}
      isChecked={isChecked}
      isIndeterminate={isIndeterminate}
      testId={isIndeterminate ? 'header-checkbox-indeterminate' : 'header-checkbox'}
      isDisabled={referencing}
      tabIndex={-1}
    />
  );
};
export default HeaderCheckbox;
